// /crear-contrasena — forced first-login password change for direct-created
// accounts. /auth/login answered step:'password_change' (the one-time temp
// password never buys a session); this page trades the 5-min challenge token +
// the person's OWN new password for the real token pair. Mirrors the /mfa
// bridge page; the password fields mirror the reset screen (eye + rules +
// strength meter). Guard: no challenge token → redirect to /login.

import { PASSWORD_RULES, passwordMeetsPolicy } from '@vysto/shared/auth/password-policy'
import { type FormEvent, useRef, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'

import { AuthLayout } from './AuthLayout.js'
import { postForcedPasswordChange } from '../../api/client.js'
import { describeError } from '../../api/describe-error.js'
import { contextFromToken, homePathForContext } from '../../auth/session.js'
import { useAuthStore } from '../../auth/store.js'
import { Alert, Button, FormField, PasswordInput } from '../../design-system/index.js'

/** Map the 4-rule policy onto the PasswordInput meter's 3 levels. */
function strengthLevel(pw: string): 'weak' | 'mid' | 'strong' {
  const met = PASSWORD_RULES.filter((r) => r.test(pw)).length
  return met <= 1 ? 'weak' : met === 2 ? 'mid' : 'strong'
}

export function ForcePasswordChangePage(): JSX.Element {
  const navigate = useNavigate()
  const challengeToken = useAuthStore((s) => s.passwordChangeToken)
  const setSession = useAuthStore((s) => s.setSession)

  // Capture on mount: setSession clears passwordChangeToken, which would
  // otherwise re-trigger the guard before the post-success navigate resolves
  // (same trick as the /mfa page).
  const initialChallengeRef = useRef(challengeToken)

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  if (initialChallengeRef.current === null) {
    return <Navigate to="/login" replace />
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault()
    const token = initialChallengeRef.current
    if (token === null) return
    setError(null)
    setSubmitting(true)
    try {
      const result = await postForcedPasswordChange(token, password)
      setSession({
        accessToken: result.data.access_token,
        refreshToken: result.data.refresh_token,
        user: result.data.user,
      })
      // Staff land on /admin, clients on /home — same rule as Login and Mfa.
      void navigate(homePathForContext(contextFromToken(result.data.access_token)), {
        replace: true,
      })
    } catch (err) {
      setError(describeError(err))
    } finally {
      setSubmitting(false)
    }
  }

  const policyOk = passwordMeetsPolicy(password)
  const matches = password.length > 0 && password === confirm

  return (
    <AuthLayout>
      <h1 className="text-[26px] font-bold tracking-[-0.025em] text-text">Crea tu contraseña</h1>
      <p className="mt-1.5 text-[13px] text-text-2">
        Entraste con una contraseña temporal. Elige la tuya para continuar; la temporal deja de
        funcionar ahora.
      </p>

      {error !== null && (
        <Alert tone="sens" className="mt-5">
          {error}
        </Alert>
      )}

      <form onSubmit={(e) => void onSubmit(e)} className="mt-6 flex flex-col gap-4" noValidate>
        <FormField label="Contraseña nueva" htmlFor="force-new-password">
          <PasswordInput
            autoComplete="new-password"
            autoFocus
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={submitting}
            rules={PASSWORD_RULES.map((r) => ({ label: r.label, met: r.test(password) }))}
            {...(password.length > 0 ? { strength: strengthLevel(password) } : {})}
          />
        </FormField>
        <FormField label="Confirmar contraseña" htmlFor="force-confirm-password">
          <PasswordInput
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            disabled={submitting}
          />
        </FormField>
        {confirm.length > 0 && !matches && (
          <p className="text-[11px] text-sens">Las contraseñas no coinciden.</p>
        )}
        <Button type="submit" block loading={submitting} disabled={!policyOk || !matches}>
          Guardar y entrar
        </Button>
      </form>
    </AuthLayout>
  )
}
